import React, { useEffect, useState, useCallback } from 'react';
import { 
  Layout, 
  Typography, 
  Space, 
  Segmented, 
  Card, 
  Row, 
  Col, 
  message, 
  Avatar,
  Tag,
  Divider,
  Spin,
  Slider,
  Radio,
  Result,
  Button
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { EnvironmentOutlined, CalendarOutlined, GlobalOutlined, ClockCircleOutlined, WalletOutlined, GiftOutlined, CreditCardOutlined, StopOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { pingMultipleServers, parseServerName } from '../utils/pingUtils';
import { PAYMENTS_ENABLED } from '../constants/featureFlags';

const { Content } = Layout;
const { Title, Text } = Typography;

const PurchaseKeyPage = () => {
  const navigate = useNavigate();
  
  // Используем контекст для получения серверов, пользователя и функции создания платежа
  const { servers, loadingStates, user, createPayment, createSubscription, t } = useApp();
  
  // Состояния для конструктора тарифа
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedTraffic, setSelectedTraffic] = useState(50);
  const [selectedPeriod, setPeriod] = useState(null);
  const [selectedBonuses, setSelectedBonuses] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('yookassa');
  const [serversWithPing, setServersWithPing] = useState([]);
  const [isPingingServers, setIsPingingServers] = useState(false);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);

  // Опции периода
  const periodOptions = [
    { label: t('purchase_period_week'), value: 'week', icon: '📅' },
    { label: t('purchase_period_month'), value: 'month', icon: '🗓️' },
    { label: t('purchase_period_year'), value: 'year', icon: '📆' },
  ];

  // Функция валидации
  const validateForm = useCallback(() => {
    if (!selectedPeriod) {
      message.error(t('purchase_error_period'));
      return false;
    }
    return true;
  }, [selectedPeriod, t]);

  // Функция расчета базовой цены
  const calculateBasePrice = useCallback(() => {
    if (!selectedPeriod || !selectedCountry) return 0;
    const server = serversWithPing.find(s => s.id === selectedCountry);
    if (!server) return 0;
    
    switch (selectedPeriod) {
      case 'week':
        return server.price_week || 0;
      case 'month':
        return server.price_month || 0;
      case 'year':
        return server.price_year || 0;
      default:
        return 0;
    }
  }, [selectedPeriod, selectedCountry, serversWithPing]);

  // Функция расчета итоговой цены с учетом бонусов
  const calculateFinalPrice = useCallback(() => {
    const basePrice = calculateBasePrice();
    const finalPrice = Math.max(0, basePrice - selectedBonuses);
    return {
      basePrice,
      bonusUsed: selectedBonuses,
      finalPrice
    };
  }, [calculateBasePrice, selectedBonuses]);

  // Максимальное количество бонусов которое можно использовать
  const getMaxBonuses = useCallback(() => {
    const basePrice = calculateBasePrice();
    const userBalance = user?.balance || 0;
    return Math.min(userBalance, basePrice);
  }, [calculateBasePrice, user?.balance]);

  // Обработчик изменения слайдера бонусов
  const handleBonusChange = useCallback((value) => {
    setSelectedBonuses(value);
  }, []);

  // Функция создания платежа или подписки
  const handleCreatePayment = useCallback(async () => {
    if (!validateForm() || !user || isCreatingPayment) {
      return;
    }

    setIsCreatingPayment(true);

    try {
      const priceInfo = calculateFinalPrice();
      
      // Если полностью оплачено бонусами, создаем подписку напрямую
      if (priceInfo.finalPrice === 0) {
        const subscriptionData = {
          user_id: user.user_id,
          server_id: selectedCountry,
          subscription_period: selectedPeriod === 'week' ? 7 : 30,
          traffic_gb: selectedTraffic,
          bonus_used: selectedBonuses
        };

        console.log('Создание подписки за бонусы с данными:', subscriptionData);
        
        const result = await createSubscription(subscriptionData);
        
        if (result && result.subscription) {
          // Списываем бонусы с баланса пользователя локально для UI
          if (user.balance >= selectedBonuses) {
            user.balance -= selectedBonuses;
          }
          
          // Переходим на главную страницу
          navigate('/');
        }
      } else {
        // Создаем платеж через выбранную платежную систему
        const paymentData = {
          user_id: user.user_id,
          server_id: selectedCountry,
          period: selectedPeriod,
          bonus_used: selectedBonuses,
          payment_method: selectedPaymentMethod
        };

        console.log('Создание платежа с данными:', paymentData);
        
        const result = await createPayment(paymentData);
        
        if (result && result.confirmation_url) {
          // Перенаправление произойдет автоматически в createPayment
        }
      }
    } catch (error) {
      console.error('Ошибка создания платежа/подписки:', error);
    } finally {
      setIsCreatingPayment(false);
    }
  }, [validateForm, user, selectedCountry, selectedPeriod, selectedTraffic, selectedBonuses, selectedPaymentMethod, calculateFinalPrice, createPayment, createSubscription, navigate, isCreatingPayment]);

  // Сброс бонусов при изменении страны или периода
  useEffect(() => {
    const maxBonuses = getMaxBonuses();
    if (selectedBonuses > maxBonuses) {
      setSelectedBonuses(maxBonuses);
    }
  }, [selectedCountry, selectedPeriod, getMaxBonuses, selectedBonuses]);

  // Сброс способа оплаты на ЮКасса при выборе недельной подписки
  useEffect(() => {
    if (selectedPeriod === 'week' && selectedPaymentMethod === 'stripe') {
      setSelectedPaymentMethod('yookassa');
    }
  }, [selectedPeriod, selectedPaymentMethod]);

  // Обработка серверов из контекста и добавление ping
  useEffect(() => {
    const processServers = async () => {
      if (servers.servers && servers.servers.length > 0) {
        setIsPingingServers(true);
        console.log('Обрабатываем серверы из контекста:', servers.servers);
        
        try {
          // Парсим названия серверов
          const serversWithParsedNames = servers.servers.map(server => {
            const { flag, country } = parseServerName(server.name);
            return {
              ...server,
              flag,
              label: country,
              value: server.id,
              price_week: server.price_week,
              price_month: server.price_month,
              price_year: server.price_year
            };
          });

          // Измеряем ping для всех серверов
          const serversWithPingData = await pingMultipleServers(serversWithParsedNames);
          setServersWithPing(serversWithPingData);
          // Автоматически выбираем первый сервер
          if (serversWithPingData.length > 0) {
            setSelectedCountry(serversWithPingData[0].id);
          }

          console.log('Серверы с ping готовы:', serversWithPingData);
        } catch (error) {
          console.error('Ошибка обработки серверов:', error);
          // В случае ошибки устанавливаем серверы без ping
          const serversWithoutPing = servers.servers.map(server => {
            const { flag, country } = parseServerName(server.name);
            return {
              ...server,
              flag,
              label: country,
              value: server.id,
              ping: 'N/A',
              price_week: server.price_week,
              price_month: server.price_month,
              price_year: server.price_year
            };
          });
          setServersWithPing(serversWithoutPing);
          // Автоматически выбираем первый сервер
          if (serversWithoutPing.length > 0) {
            setSelectedCountry(serversWithoutPing[0].id);
          }
        } finally {
          setIsPingingServers(false);
        }
      }
    };

    processServers();
  }, [servers]);

  const handleBackClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleCountrySelect = (countryValue) => {
    setSelectedCountry(countryValue);
  };

  const changePeriod = (period) => {
    setPeriod(period);
    setSelectedTraffic(period === 'week' ? 10 : 50);
  };

  useEffect(() => {
    // Настраиваем BackButton и MainButton при входе на страницу
    if (window.Telegram?.WebApp) {
      try {
        const { MainButton, BackButton } = window.Telegram.WebApp;
        
        // Показываем и настраиваем BackButton
        if (BackButton && BackButton.isVisible == false) {
          BackButton.show();
          BackButton.onClick(handleBackClick);
        }

        // Настраиваем MainButton в зависимости от валидности формы
        if (MainButton) {
          if (validateForm()) {
            if (isCreatingPayment) {
              MainButton.setText(t('purchase_btn_paying'));
              MainButton.showProgress();
            } else {
              MainButton.setText(t('purchase_btn_pay'));
              MainButton.hideProgress();
              MainButton.onClick(handleCreatePayment);
            }
            MainButton.show();
          } else {
            MainButton.hide();
          }

          // Cleanup function
          return () => {
            try {
              if (BackButton) {
                BackButton.offClick(handleBackClick);
                BackButton.hide();
              }
              if (MainButton) {
                MainButton.offClick(handleCreatePayment);
                MainButton.hideProgress();
                MainButton.hide();
              }
            } catch (error) {
              console.warn('Ошибка при скрытии кнопок:', error);
            }
          };
        }
      } catch (error) {
        console.warn('Ошибка при настройке кнопок:', error);
      }
    } 
  }, [handleBackClick, handleCreatePayment, validateForm, isCreatingPayment, t]);

  return (
    <Layout>
      <Content style={{ padding: 16 }}>

        {!PAYMENTS_ENABLED ? (
          <Result
            icon={<StopOutlined style={{ color: 'var(--tg-theme-button-color, #1677ff)' }} />}
            title={t('purchase_disabled_title')}
            subTitle={t('purchase_disabled_text')}
            extra={
              <Button type="primary" onClick={handleBackClick}>
                {t('purchase_disabled_btn')}
              </Button>
            }
          />
        ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          <div>
            <Title level={2}>
              <GlobalOutlined /> {t('purchase_title')}
            </Title>
            <Text type="secondary">
              {t('purchase_subtitle')}
            </Text>
          </div>

          {/* Выбор периода */}
          <Card 
            title={
              <Space>
                <CalendarOutlined />
                <span>{t('purchase_period_title')}</span>
              </Space>
            }
            size="small"
          >
            <Segmented
              options={periodOptions.map(option => ({
                label: (
                  <Space>
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </Space>
                ),
                value: option.value
              }))}
              value={selectedPeriod}
              onChange={changePeriod}
              block
              size="large"
            />
          </Card>

          {/* Блок использования бонусов */}
          {user?.balance > 0 && (
            <Card 
              title={
                <Space>
                  <WalletOutlined />
                  <span>{t('purchase_bonuses_title')}</span>
                  <Tag color="green">{user.balance} ₽ {t('purchase_bonuses_available')}</Tag>
                </Space>
              }
              size="small"
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Row justify="space-between" align="middle" style={{ marginBottom: '12px' }}>
                    <Col>
                      <Text type="secondary">{t('purchase_bonuses_deduct')}</Text>
                    </Col>
                    <Col>
                      <Text strong style={{ fontSize: '16px', color: 'var(--tg-theme-button-color, #1677ff)' }}>
                        {selectedBonuses} ₽
                      </Text>
                    </Col>
                  </Row>
                  
                  <Slider
                    min={0}
                    max={getMaxBonuses()}
                    value={selectedBonuses}
                    onChange={handleBonusChange}
                    step={1}
                    tooltip={{
                      formatter: (value) => `${value} ₽`
                    }}
                    trackStyle={{ backgroundColor: 'var(--tg-theme-button-color, #1677ff)' }}
                    handleStyle={{ borderColor: 'var(--tg-theme-button-color, #1677ff)' }}
                    disabled={getMaxBonuses() === 0}
                  />
                  
                  <Row justify="space-between" style={{ marginTop: '8px' }}>
                    <Col>
                      <Text type="secondary" style={{ fontSize: '12px' }}>0 ₽</Text>
                    </Col>
                    <Col>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {getMaxBonuses()} ₽ {t('purchase_bonuses_max')}
                      </Text>
                    </Col>
                  </Row>
                </div>
                
                {selectedBonuses > 0 && (
                  <Card size="small">
                    <Space>
                      <GiftOutlined style={{ color: 'var(--tg-theme-button-color, #1677ff)' }} />
                      <Text style={{ fontSize: '13px' }}>
                        {t('purchase_bonuses_saving')} <Text strong>{selectedBonuses} ₽</Text> {t('purchase_bonuses_with')}
                      </Text>
                    </Space>
                  </Card>
                )}
              </Space>
            </Card>
          )}

          {/* Выбор способа оплаты */}
          <Card 
            title={
              <Space>
                <CreditCardOutlined />
                <span>{t('purchase_payment_title')}</span>
              </Space>
            }
            size="small"
          >
            <Radio.Group 
              value={selectedPaymentMethod} 
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio.Button 
                  value="yookassa" 
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '8px',
                    ...(selectedPaymentMethod === 'yookassa' && {
                      borderColor: 'var(--tg-theme-button-color, #1677ff)',
                      backgroundColor: 'var(--tg-theme-button-color, #1677ff)',
                      color: 'var(--tg-theme-button-text-color, #fff)'
                    })
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <span style={{ fontSize: '20px' }}>🇷🇺</span>
                      <div>
                        <Text strong style={{ 
                          color: selectedPaymentMethod === 'yookassa' ? 'inherit' : undefined 
                        }}>
                          {t('purchase_yookassa')}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ 
                          fontSize: '12px',
                          color: selectedPaymentMethod === 'yookassa' ? 'rgba(255,255,255,0.8)' : undefined 
                        }}>
                          {t('purchase_yookassa_desc')}
                        </Text>
                      </div>
                    </Space>
                    <Tag color={selectedPaymentMethod === 'yookassa' ? 'green' : 'default'}>
                      RUB
                    </Tag>
                  </Space>
                </Radio.Button>
                
                <Radio.Button 
                  value="stripe" 
                  disabled={selectedPeriod === 'week'}
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '8px',
                    opacity: selectedPeriod === 'week' ? 0.5 : 1,
                    cursor: selectedPeriod === 'week' ? 'not-allowed' : 'pointer',
                    ...(selectedPaymentMethod === 'stripe' && selectedPeriod !== 'week' && {
                      borderColor: 'var(--tg-theme-button-color, #1677ff)',
                      backgroundColor: 'var(--tg-theme-button-color, #1677ff)',
                      color: 'var(--tg-theme-button-text-color, #fff)'
                    })
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <span style={{ fontSize: '20px' }}>🌍</span>
                      <div>
                        <Text strong style={{ 
                          color: selectedPaymentMethod === 'stripe' && selectedPeriod !== 'week' ? 'inherit' : undefined 
                        }}>
                          {t('purchase_stripe')}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ 
                          fontSize: '12px',
                          color: selectedPaymentMethod === 'stripe' && selectedPeriod !== 'week' ? 'rgba(255,255,255,0.8)' : undefined 
                        }}>
                          {selectedPeriod === 'week' ? t('purchase_stripe_unavailable') : t('purchase_stripe_desc')}
                        </Text>
                      </div>
                    </Space>
                    <Tag color={selectedPaymentMethod === 'stripe' && selectedPeriod !== 'week' ? 'green' : 'default'}>
                      USD/EUR
                    </Tag>
                  </Space>
                </Radio.Button>
              </Space>
            </Radio.Group>
          </Card>

          {/* Сводка выбора */}
          {selectedPeriod && (
            <Card 
              size="small"
              title={
                <Space>
                  <Text strong>
                    📋 {t('purchase_summary_title')}
                  </Text>
                </Space>
              }
            >
              <Row gutter={[16, 12]}>
                
                <Col span={12}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      <GlobalOutlined />
                      <Text strong type="primary">{t('purchase_traffic')}</Text>
                    </Space>
                    <Text strong>{selectedTraffic} GB</Text>
                  </Space>
                </Col>
                
                {selectedPeriod && (
                  <Col span={12}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Space>
                        <ClockCircleOutlined />
                        <Text strong type="primary">{t('purchase_period')}</Text>
                      </Space>
                      <Space>
                        <span>{periodOptions.find(p => p.value === selectedPeriod)?.icon}</span>
                        <Text strong>
                          {periodOptions.find(p => p.value === selectedPeriod)?.label}
                        </Text>
                      </Space>
                    </Space>
                  </Col>
                )}
                
                {selectedPeriod && (
                  <Col span={24}>
                    <Divider />
                    {(() => {
                      const priceInfo = calculateFinalPrice();
                      return (
                        <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
                          {/* Базовая цена */}
                          <Space style={{ width: '100%', justifyContent: 'center' }}>
                            <Text type="secondary">{t('purchase_base_price')}</Text>
                            <Text strong>{priceInfo.basePrice} ₽</Text>
                          </Space>
                          
                          {/* Бонусы к списанию */}
                          {priceInfo.bonusUsed > 0 && (
                            <Space style={{ width: '100%', justifyContent: 'center' }}>
                              <Text type="secondary">{t('purchase_bonuses')}</Text>
                              <Text strong style={{ color: '#52c41a' }}>-{priceInfo.bonusUsed} ₽</Text>
                            </Space>
                          )}
                          
                          {/* Итоговая цена */}
                          <Space style={{ width: '100%', justifyContent: 'center' }}>
                            <Text strong type="primary" style={{ fontSize: '18px' }}>
                              {t('purchase_total')} {priceInfo.finalPrice} ₽
                            </Text>
                          </Space>
                          
                          {priceInfo.finalPrice === 0 && (
                            <Tag color="success" style={{ marginTop: '8px' }}>
                              {t('purchase_fully_paid')}
                            </Tag>
                          )}
                        </Space>
                      );
                    })()}
                  </Col>
                )}
              </Row>
            </Card>
          )}

        </Space>
        )}
      </Content>
    </Layout>
  );
};

export default PurchaseKeyPage;
